import { useMemo, useState } from "react";

import { useField, type FieldConfig } from "formik";
import Select, { createFilter, type Props as SelectProps } from "react-select";

import { apiSlice } from "app/apiSlice";
import getCustomSelectStyles from "components/Forms/styles/getCustomSelectStyles";

const stableObject = {};

interface Creator {
  _id: string;
  name: string;
  email: string;
  isAgency?: boolean;
}

interface CreatorOption {
  value: string;
  label: string;
  name: string;
  email: string;
  isAgency?: boolean;
  isAlreadyAssigned?: boolean;
}

interface CreatorSelectProps
  extends Omit<
      SelectProps<CreatorOption, false>,
      "options" | "value" | "onBlur"
    >,
    Pick<FieldConfig, "validate"> {
  name: string;
  campaign_id?: string;
}

export const CreatorSelect = ({
  name,
  campaign_id,
  onChange,
  validate,
  ...selectProps
}: CreatorSelectProps) => {
  const [field, meta, helpers] = useField({ name, validate });

  const { data: creatorsData, isLoading: isLoadingCreators }
    = apiSlice.useGetCreatorsQuery(stableObject);

  // Fetch assignments when campaign_id is provided
  const { assignedCreatorIds, isLoading: isLoadingAssignments }
    = apiSlice.useGetAssignmentsQuery(
      { campaign_id, _source: "creator select" },
      {
        selectFromResult: ({ data, isLoading }) => ({
          assignedCreatorIds:
            data?.results?.map(
              (assignment: any) =>
                assignment?.user_id?._id ?? assignment?.user_id,
            ) || [],
          isLoading,
        }),
        skip: !campaign_id,
      },
    );

  const creatorOptions = useMemo(() => {
    if (!creatorsData?.results) return [];

    return creatorsData.results.map((creator: Creator) => ({
      value: creator._id,
      label: creator.name,
      name: creator.name,
      email: creator.email,
      isAgency: creator.isAgency,
      isAlreadyAssigned: campaign_id
        ? assignedCreatorIds.includes(creator._id)
        : false,
      _id: creator._id,
    }));
  }, [creatorsData?.results, assignedCreatorIds, campaign_id]);

  const isLoading = isLoadingCreators || isLoadingAssignments;

  const selectedOption = useMemo(() => {
    return (
      creatorOptions.find((option) => option.value === field.value) || null
    );
  }, [creatorOptions, field.value]);

  const handleChange = (
    selectedOption: CreatorOption | null,
    actionMeta: any,
  ) => {
    const newValue = selectedOption?.value || "";
    helpers.setValue(newValue);
    helpers.setTouched(true);

    // Call react-select's onChange if provided
    if (onChange) {
      onChange(selectedOption, actionMeta);
    }
  };

  // Custom filter using react-select's createFilter with proper typing
  const customFilter = useMemo(() => {
    return createFilter({
      ignoreCase: true,
      ignoreAccents: true,
      stringify: (option: any) => {
        // Include both name and email in search
        const data = option.data || option;
        const searchableText = [data.name, data.email || "", data._id]
          .filter(Boolean)
          .join(" ");

        return searchableText;
      },
    });
  }, []);

  const formatOptionLabel = (option: CreatorOption) => (
    <div>
      <div className="fw-medium d-flex align-items-center">
        {option.name}
        {option.isAgency && (
          <span className="badge bg-info text-white ms-2 small">(Agency)</span>
        )}
        {option.isAlreadyAssigned && (
          <span className="badge bg-warning text-dark ms-2 small">
            Already Assigned
          </span>
        )}
      </div>
      {option.email && <div className="text-muted small">{option.email}</div>}
    </div>
  );

  const customStyles = getCustomSelectStyles("lg");

  // Use Formik's built-in error state
  const hasError = meta.touched && !!meta.error;

  return (
    <Select
      {...selectProps}
      id={name}
      name={name}
      value={selectedOption}
      onChange={handleChange}
      onBlur={() => helpers.setTouched(true)}
      options={creatorOptions}
      placeholder={selectProps.placeholder || "Search creators..."}
      isSearchable={true}
      isLoading={isLoading}
      filterOption={customFilter}
      formatOptionLabel={formatOptionLabel}
      blurInputOnSelect={true}
      noOptionsMessage={({ inputValue }) =>
        inputValue
          ? `No creators found matching "${inputValue}"`
          : "No creators available"
      }
      isClearable={!selectProps.required}
      className={`custom-select ${hasError ? "is-invalid" : ""}`}
      classNames={{
        control: (state) => {
          let classes = "form-control";
          if (hasError) classes += " is-invalid";
          return classes;
        },
      }}
      styles={{
        control: (baseStyles, state) => ({
          ...customStyles.control(baseStyles, state),
          borderColor: hasError
            ? "var(--bs-form-invalid-border-color)"
            : state.isFocused
              ? "var(--custom-select-border-color-focus)"
              : "var(--custom-select-border-color)",
          boxShadow: hasError
            ? "var(--bs-form-invalid-focus-box-shadow)"
            : state.isFocused
              ? "var(--custom-select-focus-ring-shadow)"
              : "none",
        }),
        indicatorsContainer: customStyles.indicatorsContainer,
      }}
      aria-invalid={hasError ? "true" : "false"}
      aria-describedby={hasError ? `${name}-error` : undefined}
    />
  );
};
